import React, { useState, useRef, useCallback, useEffect } from 'react';
import DashboardShell from '../components/DashboardShell';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { Rocket, ExternalLink, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { resolveAliFullStackBase } from '../lib/alifullstack';
import { apiPath, formatApiDetail, parseResponseJson } from '../lib/http';

function readShellToken() {
  try {
    return (localStorage.getItem('token') || '').trim();
  } catch {
    return '';
  }
}

function buildLaunchUrl(base) {
  const baseNorm = (base || '/alifullstack-api').replace(/\/$/, '');
  const iframeQs = new URLSearchParams();
  if (typeof window !== 'undefined') {
    const token = readShellToken();
    if (token) iframeQs.set('token', token);
    const pageQs = new URLSearchParams(window.location.search);
    for (const key of ['test', 'run', 'prompt', 'appId', 'chatId']) {
      const v = pageQs.get(key);
      if (v) iframeQs.set(key, v);
    }
  }
  const qs = iframeQs.toString();
  return qs ? `${baseNorm}/?${qs}` : `${baseNorm}/`;
}

async function probeBuilderGateway(base) {
  const baseNorm = (base || '/alifullstack-api').replace(/\/$/, '');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);
  try {
    const res = await fetch(`${baseNorm}/healthz`, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    if (res.ok) return true;
    const root = await fetch(`${baseNorm}/`, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    return root.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function probeBackendHealth() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2500);
  try {
    const res = await fetch(apiPath('health'), {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Snag Builder as a multi-tenant web service (not Electron).
 * See services/alifullstack/PORTING_PLAN.md
 *
 * Local + production: embed same-origin /alifullstack-api (craco/server.js
 * proxy → Snag Builder gateway). Keeps auth token, chat SSE, and preview on
 * snagcart.io instead of redirecting to railway.app.
 */
const AliFullStackBuilder = ({ user }) => {
  const base = resolveAliFullStackBase();
  const src = buildLaunchUrl(base);
  const [loaded, setLoaded] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [gatewayUp, setGatewayUp] = useState(null); // null=checking, true/false
  const [backendUp, setBackendUp] = useState(null); // null=checking, true/false
  const iframeRef = useRef(null);

  const checkGateway = useCallback(async () => {
    if (!base) {
      setGatewayUp(false);
      return false;
    }
    setGatewayUp(null);
    setBackendUp(null);
    const [ok, backendOk] = await Promise.all([
      probeBuilderGateway(base),
      probeBackendHealth(),
    ]);
    setGatewayUp(ok);
    setBackendUp(backendOk);
    return ok;
  }, [base]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [ok, backendOk] = await Promise.all([
        probeBuilderGateway(base),
        probeBackendHealth(),
      ]);
      if (!cancelled) {
        setGatewayUp(ok);
        setBackendUp(backendOk);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base, nonce]);

  const handleRefresh = useCallback(async () => {
    setLoaded(false);
    const ok = await checkGateway();
    if (ok) setNonce((n) => n + 1);
  }, [checkGateway]);

  // Auth sync + AI proxy: builder iframe asks parent to call /api/ai/* with shell JWT
  // (same path Template Editor uses — avoids iframe unauthorized errors).
  useEffect(() => {
    if (gatewayUp !== true) return undefined;

    const pushAuth = () => {
      const token = readShellToken();
      if (!token || !iframeRef.current?.contentWindow) return;
      try {
        iframeRef.current.contentWindow.postMessage(
          { type: 'SNAG_PARENT_AUTH', token },
          '*',
        );
      } catch {
        /* ignore */
      }
    };

    const replyAi = (requestId, payload) => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'SNAG_AI_PROXY_RESULT', requestId, ...payload },
          '*',
        );
      } catch {
        /* ignore */
      }
    };

    const onMessage = async (event) => {
      const data = event?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'SNAG_REQUEST_AUTH') {
        pushAuth();
        return;
      }
      if (data.type !== 'SNAG_AI_PROXY') return;

      const requestId = data.requestId;
      const path = String(data.path || '');
      const body = data.body && typeof data.body === 'object' ? data.body : {};
      if (!requestId || !path.startsWith('/ai/')) {
        replyAi(requestId || 'unknown', {
          ok: false,
          status: 400,
          error: 'Invalid AI proxy request',
        });
        return;
      }

      const token = readShellToken();
      if (!token) {
        replyAi(requestId, {
          ok: false,
          status: 401,
          error: 'Sign in to Snagcart first, then reopen Snag Builder',
        });
        return;
      }

      try {
        const res = await fetch(apiPath(path.replace(/^\//, '')), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const { data: json } = await parseResponseJson(res);
        if (!res.ok) {
          replyAi(requestId, {
            ok: false,
            status: res.status,
            error: formatApiDetail(json, `AI request failed (HTTP ${res.status})`),
          });
          return;
        }
        replyAi(requestId, { ok: true, status: res.status, data: json });
      } catch (err) {
        replyAi(requestId, {
          ok: false,
          status: 0,
          error: err?.message || 'AI proxy network error',
        });
      }
    };

    window.addEventListener('message', onMessage);
    pushAuth();
    const timer = window.setInterval(pushAuth, 4000);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(timer);
    };
  }, [loaded, nonce, src, gatewayUp]);

  if (backendUp === false && gatewayUp !== true) {
    return (
      <DashboardShell user={user} fullscreen activeNav="snagbuilder" title="Snag Builder">
        <div className="flex h-full min-h-[50vh] flex-col items-center justify-center gap-3 bg-[#08080b]" data-testid="snag-builder-starting-services">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-xl shadow-lg shadow-orange-500/25"
            style={{ background: 'linear-gradient(135deg,#f97316,#f59e0b)' }}
          >
            <Rocket className="h-5 w-5 text-white -rotate-45" />
          </div>
          <p className="text-xs font-medium text-zinc-400">Starting services…</p>
          <Button
            onClick={handleRefresh}
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg px-2 text-zinc-400 hover:bg-white/[0.06] hover:text-white"
            data-testid="snag-builder-retry-backend"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>
      </DashboardShell>
    );
  }

  if (!base || gatewayUp === false) {
    return (
      <DashboardShell user={user}>
        <div className="min-h-[70vh] flex items-center justify-center p-6">
          <div
            className="relative max-w-lg w-full rounded-2xl border border-white/[0.08] bg-[#141417] p-10 text-center overflow-hidden"
            data-testid="snag-builder-unavailable"
          >
            <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-orange-500/10 blur-3xl" />
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg shadow-orange-500/20"
                 style={{ background: 'linear-gradient(135deg,#f97316,#f59e0b)' }}>
              <Rocket className="h-8 w-8 text-white -rotate-45" />
            </div>
            <h1 className="mt-5 text-2xl font-bold text-white">Snag Builder is warming up</h1>
            <p className="mt-2 text-sm text-zinc-400">
              The local builder gateway (port 8787) isn&apos;t running. Start it, then retry:
            </p>
            <code className="mt-4 inline-block rounded-lg bg-black/40 border border-white/[0.06] px-3 py-2 text-emerald-300 text-sm">
              scripts/start-alifullstack-web.ps1
            </code>
            <div className="mt-6">
              <Button
                onClick={handleRefresh}
                className="rounded-lg bg-orange-500 hover:bg-orange-400 text-white"
                data-testid="snag-builder-retry-gateway"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Retry connection
              </Button>
            </div>
          </div>
        </div>
      </DashboardShell>
    );
  }

  if (gatewayUp === null || backendUp === null) {
    return (
      <DashboardShell user={user} fullscreen activeNav="snagbuilder" title="Snag Builder">
        <div className="flex h-full min-h-[50vh] flex-col items-center justify-center gap-3 bg-[#08080b]" data-testid="snag-builder-probing">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-xl shadow-lg shadow-orange-500/25"
            style={{ background: 'linear-gradient(135deg,#f97316,#f59e0b)' }}
          >
            <Rocket className="h-5 w-5 text-white -rotate-45" />
          </div>
          <p className="text-xs font-medium text-zinc-400">
            {backendUp === false ? 'Starting services…' : 'Connecting to Snag Builder…'}
          </p>
        </div>
      </DashboardShell>
    );
  }

  const headerExtras = (
    <div className="flex items-center gap-1.5" data-testid="snag-builder-toolbar">
      <span className="mr-1 hidden items-center gap-1.5 rounded-md border border-white/[0.06] bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-zinc-400 sm:inline-flex">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
        Live
      </span>
      <Button
        onClick={handleRefresh}
        variant="ghost"
        size="sm"
        className="h-8 rounded-lg px-2 text-zinc-400 hover:bg-white/[0.06] hover:text-white"
        data-testid="snag-builder-refresh"
      >
        <RefreshCw className="h-3.5 w-3.5 sm:mr-1" />
        <span className="hidden sm:inline">Refresh</span>
      </Button>
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="h-8 rounded-lg px-2 text-zinc-400 hover:bg-white/[0.06] hover:text-white"
      >
        <a href={src} target="_blank" rel="noreferrer" data-testid="snag-builder-open-new-tab">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </Button>
    </div>
  );

  return (
    <DashboardShell
      user={user}
      fullscreen
      activeNav="snagbuilder"
      title="Snag Builder"
      headerExtras={headerExtras}
    >
      <div className="relative flex h-full min-h-0 flex-1 flex-col bg-[#08080b]">
        <div className="shrink-0 border-b border-white/[0.06] px-3 py-2">
          <ModuleDemoVideo moduleKey="snagbuilder" className="mb-0" compact />
        </div>
        <div className="relative min-h-0 flex-1">
          {!loaded && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#08080b]"
              data-testid="snag-builder-loading"
            >
              <div
                className="flex h-11 w-11 items-center justify-center rounded-xl shadow-lg shadow-orange-500/25"
                style={{ background: 'linear-gradient(135deg,#f97316,#f59e0b)' }}
              >
                <Rocket className="h-5 w-5 text-white -rotate-45" />
              </div>
              <p className="flex items-center gap-1.5 text-xs font-medium text-zinc-400">
                <Sparkles className="h-3.5 w-3.5 text-orange-400" /> Booting builder…
              </p>
            </div>
          )}
          <iframe
            key={nonce}
            ref={iframeRef}
            title="Snag Builder"
            src={src}
            onLoad={() => setLoaded(true)}
            className="h-full min-h-0 w-full border-0 bg-[#0a0a0a]"
            allow="clipboard-read; clipboard-write; cross-origin-isolated"
          />
        </div>
      </div>
    </DashboardShell>
  );
};

export default AliFullStackBuilder;
